import { useQuery } from '@tanstack/react-query';

import { authFetch } from './fetchers';
import { parseOrFallback } from './parseOrFallback';
import { cohortDetailKeys } from './queryKeys';
import { type ConceptGroupFromApi, conceptGroupSchema, listResponse } from './schemas';

interface UseConceptGroupsQueryParams {
  projectId: string;
}

/**
 * Project-scoped concept groups read for the gen-3 cohort detail page.
 * Replaces the legacy RTK `useGetConceptGroupsQuery` chain.
 *
 * Hydration into the Zustand cohort-definition store is the orchestrator's
 * responsibility (see `useCohortHydration`); this hook is a pure read.
 */
export const useConceptGroupsQuery = ({ projectId }: UseConceptGroupsQueryParams) => {
  return useQuery<ConceptGroupFromApi[]>({
    queryKey: cohortDetailKeys.conceptGroups(projectId),
    queryFn: async () => {
      const raw = await authFetch<unknown>(`/concept-groups?projectId=${projectId}`);
      const envelope = parseOrFallback(
        listResponse(conceptGroupSchema),
        raw,
        'useConceptGroupsQuery'
      );
      return envelope.data as ConceptGroupFromApi[];
    },
    enabled: !!projectId,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
  });
};
